import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ConfirmDialog } from './ConfirmDialog';
import { optimizeImage } from '@/lib/imageOptimizer';
import {
  DotsSixVertical, Trash, Plus, UploadSimple, Image, TextT, TextAlignLeft,
  Minus, TextB, TextItalic, ListBullets, Link as LinkIcon, InstagramLogo, CaretDown,
} from '@phosphor-icons/react';

const monoFont = "'IBM Plex Mono', monospace";

interface BioBlock {
  id: string;
  type: 'text' | 'rich-text' | 'photo' | 'separator';
  content: string;
  image_url: string;
  width: string;
  order: number;
}

interface ExternalLink {
  id: string;
  network: string;
  url: string;
  order: number;
}

interface AboutData {
  id?: string;
  photo_url: string | null;
  bio_blocks: BioBlock[];
  email: string;
  phone: string;
  location: string;
  links: ExternalLink[];
  status: string;
}

const NETWORK_OPTIONS = [
  'Instagram', 'Vimeo', 'YouTube', 'LinkedIn', 'TikTok', 'X/Twitter', 'Facebook', 'Site', 'Outro',
];

const BLOCK_TYPES = [
  { type: 'text', label: 'Texto', icon: TextT },
  { type: 'rich-text', label: 'Texto rico', icon: TextAlignLeft },
  { type: 'photo', label: 'Foto', icon: Image },
  { type: 'separator', label: 'Separador', icon: Minus },
] as const;

const uid = () => crypto.randomUUID();

const inputStyle: React.CSSProperties = {
  fontFamily: monoFont, fontSize: 14, fontWeight: 300,
  backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.1)',
  padding: '10px 12px', color: 'rgba(247,246,242,0.85)', width: '100%', outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase' as const,
  opacity: 0.4, color: '#F7F6F2', marginBottom: 8, display: 'block',
};

export function AdminAbout() {
  const { session } = useAuth();
  const [data, setData] = useState<AboutData>({
    photo_url: null, bio_blocks: [], email: '', phone: '', location: '', links: [], status: 'draft',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedError, setSavedError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deletePhotoConfirm, setDeletePhotoConfirm] = useState(false);
  const [deleteBlockId, setDeleteBlockId] = useState<string | null>(null);
  const [deleteLinkId, setDeleteLinkId] = useState<string | null>(null);
  const [showBlockSelector, setShowBlockSelector] = useState(false);
  const [dragBlockIdx, setDragBlockIdx] = useState<number | null>(null);
  const [dragLinkIdx, setDragLinkIdx] = useState<number | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const blockPhotoInputRef = useRef<HTMLInputElement>(null);
  const [blockPhotoTarget, setBlockPhotoTarget] = useState<string | null>(null);

  useEffect(() => {
    loadAbout();
  }, [session]);

  const loadAbout = async () => {
    if (!session) return;
    const { data: row } = await supabase
      .from('about_pages')
      .select('*')
      .eq('user_id', session.user.id)
      .maybeSingle();
    if (row) {
      setData({
        id: row.id,
        photo_url: row.photo_url,
        bio_blocks: (row.bio_blocks as any[] || []),
        email: row.email || '',
        phone: row.phone || '',
        location: row.location || '',
        links: (row.links as any[] || []),
        status: row.status,
      });
    }
    setLoading(false);
  };

  const uploadFile = async (file: File, path: string) => {
    const optimized = await optimizeImage(file);
    const { error } = await supabase.storage.from('site-assets').upload(path, optimized.file, { upsert: true });
    if (error) throw error;
    const { data: pub } = supabase.storage.from('site-assets').getPublicUrl(path);
    return pub.publicUrl + '?t=' + Date.now();
  };

  const handlePhotoUpload = async (file: File) => {
    if (!session) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${session.user.id}/about/photo.${ext}`;
      const url = await uploadFile(file, path);
      setData(d => ({ ...d, photo_url: url }));
    } catch (e) { console.error(e); }
    setUploading(false);
  };

  const handleBlockPhotoUpload = async (file: File) => {
    if (!session || !blockPhotoTarget) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${session.user.id}/about/block-${blockPhotoTarget}.${ext}`;
      const url = await uploadFile(file, path);
      setData(d => ({
        ...d,
        bio_blocks: d.bio_blocks.map(b => b.id === blockPhotoTarget ? { ...b, image_url: url } : b),
      }));
    } catch (e) { console.error(e); }
    setUploading(false);
    setBlockPhotoTarget(null);
  };

  const addBlock = (type: BioBlock['type']) => {
    const block: BioBlock = {
      id: uid(), type, content: '', image_url: '', width: '100', order: data.bio_blocks.length,
    };
    setData(d => ({ ...d, bio_blocks: [...d.bio_blocks, block] }));
    setShowBlockSelector(false);
  };

  const updateBlock = (id: string, patch: Partial<BioBlock>) => {
    setData(d => ({
      ...d,
      bio_blocks: d.bio_blocks.map(b => b.id === id ? { ...b, ...patch } : b),
    }));
  };

  const removeBlock = (id: string) => {
    setData(d => ({ ...d, bio_blocks: d.bio_blocks.filter(b => b.id !== id) }));
    setDeleteBlockId(null);
  };

  const addLink = () => {
    const link: ExternalLink = { id: uid(), network: 'Instagram', url: '', order: data.links.length };
    setData(d => ({ ...d, links: [...d.links, link] }));
  };

  const updateLink = (id: string, patch: Partial<ExternalLink>) => {
    setData(d => ({
      ...d,
      links: d.links.map(l => l.id === id ? { ...l, ...patch } : l),
    }));
  };

  const removeLink = (id: string) => {
    setData(d => ({ ...d, links: d.links.filter(l => l.id !== id) }));
    setDeleteLinkId(null);
  };

  // Drag handlers for blocks
  const handleBlockDragStart = (idx: number) => setDragBlockIdx(idx);
  const handleBlockDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragBlockIdx === null || dragBlockIdx === idx) return;
    setData(d => {
      const blocks = [...d.bio_blocks];
      const [moved] = blocks.splice(dragBlockIdx, 1);
      blocks.splice(idx, 0, moved);
      setDragBlockIdx(idx);
      return { ...d, bio_blocks: blocks.map((b, i) => ({ ...b, order: i })) };
    });
  };
  const handleBlockDragEnd = () => setDragBlockIdx(null);

  // Drag handlers for links
  const handleLinkDragStart = (idx: number) => setDragLinkIdx(idx);
  const handleLinkDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragLinkIdx === null || dragLinkIdx === idx) return;
    setData(d => {
      const links = [...d.links];
      const [moved] = links.splice(dragLinkIdx, 1);
      links.splice(idx, 0, moved);
      setDragLinkIdx(idx);
      return { ...d, links: links.map((l, i) => ({ ...l, order: i })) };
    });
  };
  const handleLinkDragEnd = () => setDragLinkIdx(null);

  const handleSave = async () => {
    if (!session) return;
    setSaving(true);
    const payload = {
      user_id: session.user.id,
      photo_url: data.photo_url,
      bio_blocks: JSON.parse(JSON.stringify(data.bio_blocks)),
      email: data.email || null,
      phone: data.phone || null,
      location: data.location || null,
      links: JSON.parse(JSON.stringify(data.links)),
      status: data.status,
      published_at: data.status === 'published' ? new Date().toISOString() : null,
    };

    let error: any = null;
    if (data.id) {
      const res = await supabase.from('about_pages').update(payload).eq('id', data.id);
      error = res.error;
    } else {
      const res = await supabase.from('about_pages').insert([payload]).select('id').single();
      error = res.error;
      if (!error && res.data) setData(d => ({ ...d, id: res.data.id }));
    }
    setSaving(false);
    if (error) {
      console.error('Erro ao salvar about:', error);
      setSavedError('erro ao salvar. tente novamente.');
      setTimeout(() => setSavedError(null), 4000);
    } else {
      setSavedError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const togglePublish = async () => {
    const nextStatus = data.status === 'published' ? 'draft' : 'published';
    setData(d => ({ ...d, status: nextStatus }));
    if (!session || !data.id) return;

    const { error } = await supabase
      .from('about_pages')
      .update({
        status: nextStatus,
        published_at: nextStatus === 'published' ? new Date().toISOString() : null,
      })
      .eq('id', data.id);

    if (error) {
      setData(d => ({ ...d, status: data.status }));
      console.error('Erro ao publicar about:', error);
      setSavedError('erro ao publicar. tente novamente.');
      setTimeout(() => setSavedError(null), 4000);
    }
  };

  if (loading) {
    return (
      <div style={{ fontFamily: monoFont, fontSize: 13, color: 'rgba(247,246,242,0.5)', padding: 32 }}>
        Carregando...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', opacity: 0.4 }}>
          ABOUT
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300,
            padding: '3px 8px',
            backgroundColor: data.status === 'published' ? 'rgba(74,222,128,0.15)' : 'rgba(247,246,242,0.06)',
            color: data.status === 'published' ? '#00E87A' : 'rgba(247,246,242,0.5)',
          }}>
            {data.status === 'published' ? 'PUBLICADO' : 'RASCUNHO'}
          </span>
          <button
            onClick={togglePublish}
            style={{
              fontFamily: monoFont, fontSize: 12, fontWeight: 300,
              padding: '6px 12px', cursor: 'pointer',
              border: '0.5px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent',
              color: 'rgba(247,246,242,0.7)',
            }}
          >
            {data.status === 'published' ? 'DESPUBLICAR' : 'PUBLICAR'}
          </button>
        </div>
      </div>

      {/* Block 1: Artist Photo */}
      <div style={{ marginBottom: 40 }}>
        <span style={labelStyle}>FOTO DO ARTISTA</span>
        {data.photo_url ? (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
            <img src={data.photo_url} alt="" style={{ width: 120, height: 120, objectFit: 'cover', border: '0.5px solid rgba(247,246,242,0.1)' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => photoInputRef.current?.click()}
                style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.6)', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
              >
                TROCAR
              </button>
              <span style={{ color: 'rgba(247,246,242,0.2)' }}>·</span>
              <button
                onClick={() => setDeletePhotoConfirm(true)}
                style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: '#FF3F2D', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
              >
                REMOVER
              </button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => photoInputRef.current?.click()}
            style={{
              width: 120, height: 120, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer',
              border: '1px dashed rgba(247,246,242,0.2)', color: 'rgba(247,246,242,0.35)',
            }}
          >
            <UploadSimple size={20} weight="thin" />
            <span style={{ fontFamily: monoFont, fontSize: 12, textTransform: 'uppercase' }}>
              {uploading ? 'Enviando...' : 'Foto do artista'}
            </span>
          </div>
        )}
        <input ref={photoInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(f); e.target.value = ''; }} />
      </div>

      <ConfirmDialog
        open={deletePhotoConfirm} onOpenChange={setDeletePhotoConfirm}
        title="Remover foto" description="Tem certeza que deseja remover a foto do artista?"
        confirmLabel="Remover" cancelLabel="Cancelar" destructive
        onConfirm={() => { setData(d => ({ ...d, photo_url: null })); setDeletePhotoConfirm(false); }}
      />

      {/* Block 2: Bio */}
      <div style={{ marginBottom: 40 }}>
        <span style={labelStyle}>BIO</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.bio_blocks.map((block, idx) => (
            <div
              key={block.id}
              draggable
              onDragStart={() => handleBlockDragStart(idx)}
              onDragOver={e => handleBlockDragOver(e, idx)}
              onDragEnd={handleBlockDragEnd}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                backgroundColor: dragBlockIdx === idx ? 'rgba(247,246,242,0.04)' : 'transparent',
                padding: '8px 0',
              }}
            >
              <DotsSixVertical size={14} weight="thin" style={{ opacity: 0.3, cursor: 'grab', marginTop: 4, flexShrink: 0 }} />
              <span style={{ fontFamily: monoFont, fontSize: 12, opacity: 0.3, textTransform: 'uppercase', width: 80, flexShrink: 0, marginTop: 4 }}>
                {block.type === 'text' ? 'TEXTO' : block.type === 'rich-text' ? 'TEXTO RICO' : block.type === 'photo' ? 'FOTO' : 'SEPARADOR'}
              </span>
              <div style={{ flex: 1 }}>
                {block.type === 'text' && (
                  <textarea
                    value={block.content}
                    onChange={e => updateBlock(block.id, { content: e.target.value })}
                    placeholder="Escreva aqui..."
                    style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                  />
                )}
                {block.type === 'rich-text' && (
                  <div>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 4, padding: '4px 0' }}>
                      <button style={{ border: '0.5px solid rgba(247,246,242,0.1)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)', padding: '2px 6px', cursor: 'pointer' }}
                        onClick={() => {
                          const sel = window.getSelection();
                          if (!sel || sel.rangeCount === 0) return;
                          document.execCommand('bold');
                        }}>
                        <TextB size={12} weight="thin" />
                      </button>
                      <button style={{ border: '0.5px solid rgba(247,246,242,0.1)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)', padding: '2px 6px', cursor: 'pointer' }}
                        onClick={() => document.execCommand('italic')}>
                        <TextItalic size={12} weight="thin" />
                      </button>
                      <button style={{ border: '0.5px solid rgba(247,246,242,0.1)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)', padding: '2px 6px', cursor: 'pointer' }}
                        onClick={() => document.execCommand('insertUnorderedList')}>
                        <ListBullets size={12} weight="thin" />
                      </button>
                      <button style={{ border: '0.5px solid rgba(247,246,242,0.1)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)', padding: '2px 6px', cursor: 'pointer' }}
                        onClick={() => {
                          const url = prompt('URL do link:');
                          if (url) document.execCommand('createLink', false, url);
                        }}>
                        <LinkIcon size={12} weight="thin" />
                      </button>
                    </div>
                    <div
                      contentEditable
                      dangerouslySetInnerHTML={{ __html: block.content }}
                      onBlur={e => updateBlock(block.id, { content: e.currentTarget.innerHTML })}
                      style={{ ...inputStyle, minHeight: 80 }}
                      suppressContentEditableWarning
                    />
                  </div>
                )}
                {block.type === 'photo' && (
                  <div>
                    {block.image_url ? (
                      <img src={block.image_url} alt="" style={{ width: `${block.width}%`, maxWidth: '100%', border: '0.5px solid rgba(247,246,242,0.1)' }} />
                    ) : (
                      <div
                        onClick={() => { setBlockPhotoTarget(block.id); blockPhotoInputRef.current?.click(); }}
                        style={{
                          width: '100%', height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: '1px dashed rgba(247,246,242,0.2)', color: 'rgba(247,246,242,0.35)', cursor: 'pointer',
                          fontFamily: monoFont, fontSize: 12,
                        }}
                      >
                        + FOTO
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      {['25', '50', '75', '100'].map(w => (
                        <button key={w}
                          onClick={() => updateBlock(block.id, { width: w })}
                          style={{
                            fontFamily: monoFont, fontSize: 12, padding: '2px 8px', cursor: 'pointer',
                            border: '0.5px solid rgba(247,246,242,0.15)',
                            backgroundColor: block.width === w ? 'rgba(247,246,242,0.12)' : 'transparent',
                            color: block.width === w ? '#F7F6F2' : 'rgba(247,246,242,0.4)',
                          }}
                        >
                          {w}%
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {block.type === 'separator' && (
                  <div style={{ height: 1, backgroundColor: 'rgba(247,246,242,0.08)', margin: '8px 0' }} />
                )}
              </div>
              <button
                onClick={() => setDeleteBlockId(block.id)}
                style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: 4, marginTop: 2, flexShrink: 0 }}
              >
                <Trash size={14} weight="thin" color="#FF3F2D" />
              </button>
            </div>
          ))}
        </div>

        {/* Block selector */}
        <div style={{ marginTop: 12 }}>
          {showBlockSelector ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {BLOCK_TYPES.map(bt => (
                <button key={bt.type}
                  onClick={() => addBlock(bt.type)}
                  style={{
                    fontFamily: monoFont, fontSize: 12, fontWeight: 300, padding: '6px 12px',
                    border: '0.5px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent',
                    color: 'rgba(247,246,242,0.6)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <bt.icon size={12} weight="thin" /> {bt.label}
                </button>
              ))}
              <button
                onClick={() => setShowBlockSelector(false)}
                style={{ fontFamily: monoFont, fontSize: 12, padding: '6px 8px', border: 'none', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.3)', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowBlockSelector(true)}
              style={{
                fontFamily: monoFont, fontSize: 12, fontWeight: 300, padding: '8px 16px',
                border: '0.5px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent',
                color: 'rgba(247,246,242,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <Plus size={12} weight="thin" /> ADICIONAR BLOCO
            </button>
          )}
        </div>

        <input ref={blockPhotoInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleBlockPhotoUpload(f); e.target.value = ''; }} />
      </div>

      <ConfirmDialog
        open={!!deleteBlockId} onOpenChange={() => setDeleteBlockId(null)}
        title="Remover bloco" description="Tem certeza que deseja remover este bloco?"
        confirmLabel="Remover" cancelLabel="Cancelar" destructive
        onConfirm={() => { if (deleteBlockId) removeBlock(deleteBlockId); }}
      />

      {/* Block 3: Contact */}
      <div style={{ marginBottom: 40 }}>
        <span style={labelStyle}>CONTATO</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            type="email" value={data.email} onChange={e => setData(d => ({ ...d, email: e.target.value }))}
            placeholder="email@exemplo.com" style={inputStyle}
          />
          <input
            type="tel" value={data.phone} onChange={e => setData(d => ({ ...d, phone: e.target.value }))}
            placeholder="+55 11 99999-9999" style={inputStyle}
          />
          <input
            type="text" value={data.location} onChange={e => setData(d => ({ ...d, location: e.target.value }))}
            placeholder="São Paulo, Brasil" style={inputStyle}
          />
        </div>
      </div>

      {/* Block 4: External Links */}
      <div style={{ marginBottom: 40 }}>
        <span style={labelStyle}>LINKS</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.links.map((link, idx) => (
            <div
              key={link.id}
              draggable
              onDragStart={() => handleLinkDragStart(idx)}
              onDragOver={e => handleLinkDragOver(e, idx)}
              onDragEnd={handleLinkDragEnd}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <DotsSixVertical size={14} weight="thin" style={{ opacity: 0.3, cursor: 'grab', flexShrink: 0 }} />
              <select
                value={link.network}
                onChange={e => updateLink(link.id, { network: e.target.value })}
                style={{ ...inputStyle, width: 140, flexShrink: 0 }}
              >
                {NETWORK_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <input
                type="url" value={link.url}
                onChange={e => updateLink(link.id, { url: e.target.value })}
                placeholder="https://..." style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={() => setDeleteLinkId(link.id)}
                style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0 }}
              >
                <Trash size={14} weight="thin" color="#FF3F2D" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addLink}
          style={{
            fontFamily: monoFont, fontSize: 12, fontWeight: 300, padding: '8px 16px', marginTop: 12,
            border: '0.5px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent',
            color: 'rgba(247,246,242,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <Plus size={12} weight="thin" /> ADICIONAR LINK
        </button>
      </div>

      <ConfirmDialog
        open={!!deleteLinkId} onOpenChange={() => setDeleteLinkId(null)}
        title="Remover link" description="Tem certeza que deseja remover este link?"
        confirmLabel="Remover" cancelLabel="Cancelar" destructive
        onConfirm={() => { if (deleteLinkId) removeLink(deleteLinkId); }}
      />

      {/* Sticky Save */}
      <div style={{
        position: 'sticky', bottom: 0, backgroundColor: '#0A0A0A', padding: '16px 0',
        borderTop: '0.5px solid rgba(247,246,242,0.08)',
      }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            fontFamily: monoFont, fontSize: 13, fontWeight: 300, width: '100%',
            padding: '12px 0', cursor: saving ? 'wait' : 'pointer',
            border: '0.5px solid rgba(247,246,242,0.25)',
            backgroundColor: 'rgba(247,246,242,0.06)', color: '#F7F6F2',
          }}
        >
          {saved ? 'SALVO ✓' : saving ? 'SALVANDO...' : 'SALVAR ABOUT'}
        </button>
        {savedError && (
          <p style={{ fontFamily: monoFont, fontSize: 12, color: '#FF3F2D', marginTop: 8 }}>{savedError}</p>
        )}
      </div>
    </div>
  );
}
